import { useCallback, useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../api/endpoints';

// Normalise the snake_case /bulk-download-precheck envelope into the camelCase
// shape EnrichmentDownloadDialog consumes. `includeContacts` adds the contact
// dimension (TD-2270); the download A/B fields are shared with the bulk-export
// confirmation modal. In the company scope `net_new` is 1/0 and a null limit
// means uncapped (no box); `companyCount` is read by the project variant only.
const normalisePreview = (data) => ({
  contactCount: data?.contact_count ?? 0,
  companyCount: data?.total ?? 0,
  alreadyDownloaded: data?.already_downloaded ?? 0,
  netNew: data?.net_new ?? 0,
  currentCount: data?.current_count ?? 0,
  limit: data?.limit ?? null,
  remainingAfter: data?.remaining_after ?? null,
  allowed: data?.allowed !== false,
  cachedEmails: data?.cached_emails ?? 0,
  cachedPhones: data?.cached_phones ?? 0,
  // TD-2331/TD-2332/TD-2527: full per-bucket breakdown [{bucket,label,count}]
  // driving the "Filter by role" options. Present only when the BE preview
  // includes it (enrichment-eligible user).
  roleBuckets: (data?.role_buckets ?? []).map((b) => ({
    bucket: b?.bucket,
    label: b?.label,
    count: b?.count ?? 0
  }))
});

/**
 * Download preview for the enrichment contacts dialogs (project and company
 * scope): POST /bulk-download-precheck with includeContacts when the dialog
 * opens, re-fetched with `roleBuckets` whenever the role selection changes, and
 * fully reset on close (incl. the role selection, so a re-open starts
 * unfiltered with fresh options).
 *
 * The fetch is fail-open: a precheck blip degrades the counters but never
 * blocks a legitimate download (the export re-checks the limit server-side).
 *
 * @param {object} cfg
 * @param {boolean} cfg.isOpen
 * @param {'project'|'company'} cfg.source
 * @param {string} [cfg.sourceId]  projectId or companyId; no fetch without it
 * @param {Function} cfg.makeRequest  the store's request helper
 * @returns {{ preview: object|null, previewLoading: boolean, dialogProps: object }}
 *   `dialogProps` is spread onto EnrichmentDownloadDialog.
 */
const useContactsDownloadPreview = ({ isOpen, source, sourceId, makeRequest }) => {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  // TD-2332/TD-2527 role filter: the FULL role_buckets breakdown from the first
  // (unfiltered) preview drives the option list + counts and stays stable while
  // the user toggles; `selectedRoleBuckets` is the checked subset (empty = all);
  // `roleFilterLoading` covers the count-refresh re-fetch.
  const [roleOptions, setRoleOptions] = useState(null);
  // TD-2956: the FULL contact count, likewise captured from the first
  // (unfiltered) preview. It is the "of M" denominator, and it cannot be summed
  // from the per-bucket counts: a shareholder who also manages belongs to two
  // buckets, so those counts add up to more contacts than there are.
  const [roleTotalContacts, setRoleTotalContacts] = useState(null);
  const [selectedRoleBuckets, setSelectedRoleBuckets] = useState([]);
  const [roleFilterLoading, setRoleFilterLoading] = useState(false);

  // Monotonic request id: the latest fetch (initial open OR a role-filter
  // re-fetch) wins, so a slow earlier response can't clobber a newer one.
  const reqIdRef = useRef(0);

  const fetchPreview = useCallback(
    (roleBucketKeys, { initial }) => {
      if (!sourceId) return;
      const rid = ++reqIdRef.current;
      if (initial) {
        setPreview(null);
        setPreviewFailed(false);
        setPreviewLoading(true);
      } else {
        setRoleFilterLoading(true);
      }
      const body = { source, sourceId, includeContacts: true };
      if (roleBucketKeys.length) body.roleBuckets = roleBucketKeys;
      // Fail-open: a failed first load hides the counters, a failed refresh keeps
      // the last ones; the export still enforces the limit server-side.
      const failPreview = () => {
        if (rid !== reqIdRef.current) return;
        if (initial) setPreviewFailed(true);
        setPreviewLoading(false);
        setRoleFilterLoading(false);
      };
      makeRequest({
        endpoint: API_ENDPOINTS.BULK_DOWNLOAD_PRECHECK,
        body,
        onSuccess: (data) => {
          // `degraded`: the BE could not load the contacts and priced the whole
          // source instead, without contact counts. That quote can refuse an
          // export that fits, so it is handled like a failed precheck.
          if (data?.degraded) {
            failPreview();
            return;
          }
          if (rid !== reqIdRef.current) return;
          const normalised = normalisePreview(data);
          setPreview(normalised);
          // Options come from the unfiltered load and stay fixed thereafter, so
          // the per-bucket counts don't shift as the user narrows the selection.
          // Every later response carries the FILTERED contact count, so the
          // unfiltered total has to be taken here or not at all (TD-2956).
          if (initial) {
            setRoleOptions(normalised.roleBuckets);
            setRoleTotalContacts(normalised.contactCount);
          }
          setPreviewLoading(false);
          setRoleFilterLoading(false);
        },
        onError: failPreview
      });
    },
    [source, sourceId, makeRequest]
  );

  // Reset selection reference-stably (return the same [] when already empty) so
  // a closed render doesn't churn a new array on every pass.
  useEffect(() => {
    if (!isOpen || !sourceId) {
      reqIdRef.current += 1; // invalidate any in-flight response
      setPreview(null);
      setPreviewLoading(false);
      setPreviewFailed(false);
      setRoleOptions(null);
      setRoleTotalContacts(null);
      setSelectedRoleBuckets((prev) => (prev.length ? [] : prev));
      setRoleFilterLoading(false);
      return;
    }
    setSelectedRoleBuckets((prev) => (prev.length ? [] : prev));
    fetchPreview([], { initial: true });
    // Key on open/source changes only. fetchPreview is derived from the source +
    // the store's makeRequest (a stable instance method in prod); keying the
    // effect on its identity would re-run on any store re-render and — combined
    // with the reset above — spin into a render loop under test doubles that
    // return a fresh makeRequest each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, source, sourceId]);

  // Selection change → store the checked keys and re-fetch filtered counts /
  // cached credits (options stay put). Empty keys = all (no roleBuckets param).
  const handleRoleBucketsChange = useCallback(
    (nextKeys) => {
      setSelectedRoleBuckets(nextKeys);
      fetchPreview(nextKeys, { initial: false });
    },
    [fetchPreview]
  );

  return {
    preview,
    previewLoading,
    dialogProps: {
      downloadPreview: preview,
      previewLoading,
      previewFailed,
      roleBuckets: roleOptions,
      roleTotal: roleTotalContacts,
      selectedRoleBuckets,
      onRoleBucketsChange: handleRoleBucketsChange,
      roleFilterLoading
    }
  };
};

export default useContactsDownloadPreview;
